import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ExpenseEntry } from '../types';
import { nanoid } from 'nanoid';

interface ExpenseStore {
  expenses: ExpenseEntry[];
  defaultVatRate: number; // Default VAT rate for new expenses
  addExpense: (expense: Omit<ExpenseEntry, 'id'>) => void;
  updateExpense: (id: string, expense: Partial<ExpenseEntry>) => void;
  removeExpense: (id: string) => void;
  setDefaultVatRate: (rate: number) => void;
  calculateTotalExpenses: () => number;
  reset: () => void;
}

export const useExpenseStore = create<ExpenseStore>()(
  persist(
    (set, get) => ({
      expenses: [],
      defaultVatRate: 20, // Default 20% VAT rate

      addExpense: (expense) => {
        const newExpense: ExpenseEntry = {
          ...expense,
          id: nanoid(),
        };
        
        set((state) => ({
          expenses: [...state.expenses, newExpense],
        }));
      },

      updateExpense: (id, updates) => {
        set((state) => ({
          expenses: state.expenses.map((expense) =>
            expense.id === id ? { ...expense, ...updates } : expense
          ),
        }));
      },

      removeExpense: (id) => {
        set((state) => ({
          expenses: state.expenses.filter((expense) => expense.id !== id),
        }));
      },

      setDefaultVatRate: (rate) => {
        set({ defaultVatRate: rate });
      },

      calculateTotalExpenses: () => {
        const { expenses } = get();
        return expenses.reduce((total, expense) => total + expense.amount, 0);
      },

      reset: () => {
        set({ expenses: [], defaultVatRate: 20 });
      },
    }),
    {
      name: 'broiler_expense_data',
      partialize: (state) => ({
        expenses: state.expenses,
        defaultVatRate: state.defaultVatRate,
      }),
    }
  )
);
